import { format, subDays } from 'date-fns';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { getChannelSeriesColor } from '../../utils/colors';
import { AdPerformanceRow } from './metaHackathon-performance';
import {
  AssetSeries,
  ColorMode,
  ColorModeType,
  CreativeDiversityInsight,
  CreativeFatigueInsight,
  DIVERSITY_ARCHETYPES,
  DiversityArchetype,
  DiversityArchetypeType,
  EDGE_PADDING,
  MOCK_ASSET_COUNT,
  MOCK_CAMPAIGN_COUNT,
  MOCK_DATE_COUNT,
  QUADRANT_MIDPOINT,
  QuadrantLabel,
  QuadrantLabelType
} from './metaHackathon-constants';

// ---------------------------------------------------------------------------
// Parsing the real endpoint shapes
//
// Entity = adset. Fatigue's `creative_fatigue_summary[].ad_object_id` is the
// adset id, which equals the diversity payload's `adset_id` — that's the join.
// The fatigue time series lives in `performance_summary.historical_metric_values`.
// ---------------------------------------------------------------------------

interface RawFatiguePoint {
  assetId: string; // adset id
  date: string;
  rawFatigue: number;
}

export interface DiversityInfo {
  score?: number; // today's snapshot (undefined when "n/a")
  label: string;
  repName?: string; // representative ad name from creative_diversity_data
}

export interface AssetSeriesBundle {
  series: AssetSeries[];
  dates: string[];
  usedMock: boolean;
}

const toStringId = (val: unknown): string | undefined => {
  if (typeof val === 'string' && val.length > 0) return val;
  if (typeof val === 'number' && Number.isFinite(val)) return String(val);
  return undefined;
};

const toDate = (val: unknown): string | undefined => {
  if (typeof val !== 'string' || val.length === 0) return undefined;
  const match = val.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : undefined;
};

const toScore = (val: unknown): number | undefined => {
  const num = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(num) ? num : undefined;
};

export const parseFatigueSeries = (fatigue: CreativeFatigueInsight[]): RawFatiguePoint[] => {
  const rows: RawFatiguePoint[] = [];

  (fatigue ?? []).forEach(insight => {
    const summaries = Array.isArray(insight?.creative_fatigue_summary)
      ? insight.creative_fatigue_summary
      : [];

    summaries.forEach(entry => {
      const assetId = toStringId(entry?.ad_object_id);
      const history = entry?.performance_summary?.historical_metric_values;
      if (!assetId || !Array.isArray(history)) return;

      history.forEach(point => {
        const date = toDate(point?.timestamp);
        const value = toScore(point?.value);
        if (date && value !== undefined) {
          rows.push({ assetId, date, rawFatigue: value });
        }
      });
    });
  });

  return rows;
};

export const parseDiversity = (
  diversity: CreativeDiversityInsight[]
): Map<string, DiversityInfo> => {
  const byAdset = new Map<string, DiversityInfo>();
  (diversity ?? []).forEach(item => {
    const id = toStringId(item?.adset_id);
    if (!id) return;
    const names = (item?.creative_diversity_data ?? [])
      .map(d => d?.name)
      .filter((name): name is string => Boolean(name));
    byAdset.set(id, {
      score: toScore(item?.creative_diversity_score),
      label: item?.creative_diversity_label ?? 'n/a',
      repName: names[0]
    });
  });
  return byAdset;
};

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

// Keep a value inside the padded interior band so points never sit on the axis
// edge (where they'd be clipped by the frame).
const clampToBand = (val: number): number =>
  Math.min(1 - EDGE_PADDING, Math.max(EDGE_PADDING, val));

// Scale a raw value into the padded band [EDGE_PADDING, 1 - EDGE_PADDING].
// Falls back to the midpoint when the range is degenerate (all values equal),
// so a single date still renders sensibly.
const normalizeTo01 = (val: number, min: number, max: number): number =>
  max > min
    ? EDGE_PADDING + (1 - 2 * EDGE_PADDING) * ((val - min) / (max - min))
    : QUADRANT_MIDPOINT;

const LCG_MODULUS = 2147483647; // 2^31 - 1 (Mersenne prime)

// Deterministic polynomial string hash → seed, so an asset always gets the same
// archetype/baseline and points don't jitter between renders. Multiply-modulo
// only (no bitwise ops).
const hashStringToSeed = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) % LCG_MODULUS;
  }
  return hash + 1; // keep the seed strictly positive
};

// Park–Miller minimal-standard PRNG → repeatable [0,1) sequence from a seed.
const makeSeededRng = (seed: number): (() => number) => {
  let state = seed % LCG_MODULUS;
  if (state <= 0) {
    state += LCG_MODULUS - 1;
  }
  return () => {
    state = (state * 16807) % LCG_MODULUS;
    return (state - 1) / (LCG_MODULUS - 1);
  };
};

// ---------------------------------------------------------------------------
// Mocked diversity-over-time
// ---------------------------------------------------------------------------

const TRAJECTORY_SPREAD = 0.35; // rise/fall travels baseline ± this across the timeline
const OSC_AMPLITUDE = 0.25;
const OSC_CYCLES = 1.5;

const pickArchetype = (assetId: string): DiversityArchetypeType =>
  DIVERSITY_ARCHETYPES[hashStringToSeed(assetId) % DIVERSITY_ARCHETYPES.length];

// Diversity value at normalized time `t` (0 = first date, 1 = last date).
const diversityAt = (archetype: DiversityArchetypeType, baseline: number, t: number): number => {
  switch (archetype) {
    case DiversityArchetype.RISING:
      return clampToBand(baseline - TRAJECTORY_SPREAD + 2 * TRAJECTORY_SPREAD * t);
    case DiversityArchetype.FALLING:
      return clampToBand(baseline + TRAJECTORY_SPREAD - 2 * TRAJECTORY_SPREAD * t);
    case DiversityArchetype.OSCILLATING:
      return clampToBand(baseline + OSC_AMPLITUDE * Math.sin(2 * Math.PI * OSC_CYCLES * t));
    case DiversityArchetype.STATIC:
    default:
      return clampToBand(baseline);
  }
};

// ---------------------------------------------------------------------------
// Adapter: raw responses → normalized AssetSeries[] (with mock fallback)
// ---------------------------------------------------------------------------

export const buildAssetSeries = (
  fatigue: CreativeFatigueInsight[],
  diversity: CreativeDiversityInsight[]
): AssetSeriesBundle => {
  const rows = parseFatigueSeries(fatigue);
  if (rows.length === 0) return generateMockDataset();

  const dates = [...new Set(rows.map(row => row.date))].sort();
  const fatigueMin = Math.min(...rows.map(row => row.rawFatigue));
  const fatigueMax = Math.max(...rows.map(row => row.rawFatigue));

  // Real per-adset diversity snapshot (today's score). The over-time movement
  // is still mocked via the archetype, seeded around this real baseline.
  const diversityByAdset = parseDiversity(diversity);
  const realScores = [...diversityByAdset.values()]
    .map(info => info.score)
    .filter((score): score is number => score !== undefined);
  const divMin = realScores.length ? Math.min(...realScores) : 0;
  const divMax = realScores.length ? Math.max(...realScores) : 1;

  const byAsset = new Map<string, RawFatiguePoint[]>();
  rows.forEach(row => {
    const existing = byAsset.get(row.assetId);
    if (existing) {
      existing.push(row);
    } else {
      byAsset.set(row.assetId, [row]);
    }
  });

  const series: AssetSeries[] = [...byAsset.entries()].map(([assetId, assetRows]) => {
    const archetype = pickArchetype(assetId);
    const rng = makeSeededRng(hashStringToSeed(assetId));
    const info = diversityByAdset.get(assetId);
    const baseline =
      info?.score !== undefined ? normalizeTo01(info.score, divMin, divMax) : 0.3 + rng() * 0.4;

    const fatigueByDate = new Map(
      assetRows.map(row => [row.date, normalizeTo01(row.rawFatigue, fatigueMin, fatigueMax)])
    );

    // Forward-fill fatigue across the global timeline for dates this asset
    // is missing, so every asset has a point on every frame.
    let lastFatigue = QUADRANT_MIDPOINT;
    const points = dates.map((date, i) => {
      const t = dates.length > 1 ? i / (dates.length - 1) : 0;
      lastFatigue = fatigueByDate.get(date) ?? lastFatigue;
      return { date, fatigue: lastFatigue, diversity: diversityAt(archetype, baseline, t) };
    });

    return {
      assetId,
      assetName: info?.repName ?? assetId,
      campaign: '', // resolved from SCP performance, joined by ad_group_id
      diversityLabel: info?.label ?? 'n/a',
      archetype,
      points
    };
  });

  return { series, dates, usedMock: false };
};

export const generateMockDataset = (): AssetSeriesBundle => {
  const today = new Date();
  const dates = Array.from({ length: MOCK_DATE_COUNT }, (_, i) =>
    format(subDays(today, MOCK_DATE_COUNT - 1 - i), 'yyyy-MM-dd')
  );

  const series: AssetSeries[] = Array.from({ length: MOCK_ASSET_COUNT }, (_, assetIdx) => {
    const assetId = `mock-asset-${assetIdx + 1}`;
    const rng = makeSeededRng(hashStringToSeed(assetId));
    // Cycle archetypes by index so all four are visible in the demo.
    const archetype = DIVERSITY_ARCHETYPES[assetIdx % DIVERSITY_ARCHETYPES.length];
    const baseline = 0.25 + rng() * 0.5;

    let fatigue = rng();
    const points = dates.map((date, i) => {
      const t = dates.length > 1 ? i / (dates.length - 1) : 0;
      fatigue = clampToBand(fatigue + (rng() - 0.5) * 0.15); // gentle random walk
      return { date, fatigue, diversity: diversityAt(archetype, baseline, t) };
    });

    const diversityLabel = (() => {
      if (baseline >= 0.66) return 'HIGH';
      if (baseline >= 0.33) return 'MEDIUM';
      return 'LOW';
    })();
    return {
      assetId,
      assetName: `Creative ${assetIdx + 1}`,
      campaign: `Campaign ${(assetIdx % MOCK_CAMPAIGN_COUNT) + 1}`,
      diversityLabel,
      archetype,
      points
    };
  });

  return { series, dates, usedMock: true };
};

// ---------------------------------------------------------------------------
// Chart-time helpers
// ---------------------------------------------------------------------------

// Linearly interpolate an asset's (fatigue, diversity) at a fractional cursor
// over date indices, for smooth tweening between frames.
export const interpolatePoint = (
  series: AssetSeries,
  cursor: number
): { fatigue: number; diversity: number } => {
  const { points } = series;
  if (points.length === 0) return { fatigue: QUADRANT_MIDPOINT, diversity: QUADRANT_MIDPOINT };

  const maxIndex = points.length - 1;
  const clamped = Math.min(maxIndex, Math.max(0, cursor));
  const lowIndex = Math.floor(clamped);
  const highIndex = Math.min(maxIndex, lowIndex + 1);
  const frac = clamped - lowIndex;
  const low = points[lowIndex];
  const high = points[highIndex];

  return {
    fatigue: low.fatigue + (high.fatigue - low.fatigue) * frac,
    diversity: low.diversity + (high.diversity - low.diversity) * frac
  };
};

// Narrow a bundle to the dates within [range.start, range.end] (inclusive),
// slicing each asset's points to the same window so the cursor stays aligned.
// Returns the full bundle unchanged when the range is empty or doesn't overlap.
export const sliceBundleToRange = (
  bundle: AssetSeriesBundle,
  range: DateRange
): AssetSeriesBundle => {
  const { dates, series } = bundle;
  if (!range.start || !range.end || dates.length === 0) return bundle;

  const lo = dates.findIndex(date => date >= range.start);
  if (lo === -1) return bundle;
  let hi = dates.length - 1;
  while (hi >= lo && dates[hi] > range.end) hi -= 1;
  if (hi < lo) return bundle;

  // Full range selected — nothing to slice.
  if (lo === 0 && hi === dates.length - 1) return bundle;

  return {
    usedMock: bundle.usedMock,
    dates: dates.slice(lo, hi + 1),
    series: series.map(s => ({ ...s, points: s.points.slice(lo, hi + 1) }))
  };
};

// Resolve an asset's campaign: prefer the real SCP campaign (keyed by adset),
// fall back to any campaign synthesized at build time, else a placeholder.
export const campaignForAsset = (
  series: AssetSeries,
  performance?: Map<string, AdPerformanceRow>
): string => performance?.get(series.assetId)?.campaign || series.campaign || 'Unknown campaign';

// The grouping key an asset is colored by, per the selected color mode.
export const groupKeyForAsset = (
  series: AssetSeries,
  colorMode: ColorModeType,
  performance?: Map<string, AdPerformanceRow>
): string => {
  switch (colorMode) {
    case ColorMode.DIVERSITY:
      return series.diversityLabel || 'n/a';
    case ColorMode.AD_GROUP:
      return series.assetName || series.assetId;
    case ColorMode.CAMPAIGN:
    default:
      return campaignForAsset(series, performance);
  }
};

// Stable color per asset for the chosen color mode: unique group keys (sorted)
// are assigned brand-palette colors, so a group keeps its color as others toggle.
export const buildAssetColorMap = (
  series: AssetSeries[],
  colorMode: ColorModeType,
  performance?: Map<string, AdPerformanceRow>
): Map<string, string> => {
  const groupKeys = [
    ...new Set(series.map(s => groupKeyForAsset(s, colorMode, performance)))
  ].sort();
  const groupColor = new Map(groupKeys.map((key, i) => [key, getChannelSeriesColor(i)]));
  return new Map(
    series.map(s => [s.assetId, groupColor.get(groupKeyForAsset(s, colorMode, performance)) ?? ''])
  );
};

export const quadrantOf = (fatigue: number, diversity: number): QuadrantLabelType => {
  const highFatigue = fatigue >= QUADRANT_MIDPOINT;
  const highDiversity = diversity >= QUADRANT_MIDPOINT;
  if (highDiversity && highFatigue) return QuadrantLabel.HIGH_DIVERSITY_HIGH_FATIGUE;
  if (highDiversity) return QuadrantLabel.HIGH_DIVERSITY_LOW_FATIGUE;
  if (highFatigue) return QuadrantLabel.LOW_DIVERSITY_HIGH_FATIGUE;
  return QuadrantLabel.LOW_DIVERSITY_LOW_FATIGUE;
};
